import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StatsCard } from '../components/ui/stats-card';
import { Skeleton } from '../components/ui/skeleton';
import { ResponsiveSupplierList } from '../components/ui/responsive-supplier-list';
import { Pagination } from '../components/ui/pagination';
import { StatusBadge } from '../components/ui/status-badge';
import {
  Search,
  Plus,
  Filter,
  Download,
  X,
  MapPin,
  User,
  Mail,
  Phone,
  Package,
  Loader2,
  Users,
  UserCheck,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Calendar,
  CreditCard,
  TrendingUp
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Supplier, SupplierAPI, AdminSupplierAPI, SupplierCreateRequest, SupplierStats, OrderAPI } from '../lib/api';
import { toast } from '../components/ui/use-toast';
import OrderDetailModal from '../components/orders/OrderDetailModal';
import SupplierProductCreateModalForSupplier from '../components/supplier-products/SupplierProductCreateModalForSupplier';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";



export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deactivateSupplierId, setDeactivateSupplierId] = useState<string | null>(null);
  const [isToggling, setIsToggling] = useState<string | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [supplierStats, setSupplierStats] = useState<SupplierStats | null>(null);
  const [verificationStatusFilter, setVerificationStatusFilter] = useState<string>('all');
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('businessName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const pageSize = 10;

  // Product-related states
  const [supplierProducts, setSupplierProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);

  // Order-related states
  const [supplierOrders, setSupplierOrders] = useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderCurrentPage, setOrderCurrentPage] = useState(1);
  const [orderTotalPages, setOrderTotalPages] = useState(1);
  const [orderTotalCount, setOrderTotalCount] = useState(0);
  const orderPageSize = 10;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [newSupplier, setNewSupplier] = useState<{
    businessName: string;
    ownerName: string;
    email: string;
    mobile: string;
    password: string;
    gstNumber: string;
    panNumber: string;
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  }>({
    businessName: '',
    ownerName: '',
    email: '',
    mobile: '',
    password: '',
    gstNumber: '',
    panNumber: '',
    street: '',
    city: '',
    state: '',
    country: 'India',
    pincode: ''
  });

  // Fetch suppliers on component mount or when search/page/filters change
  useEffect(() => {
    fetchSuppliers();
  }, [currentPage, debouncedSearchQuery, verificationStatusFilter, activeStatusFilter, sortBy, sortOrder]);

  // Fetch supplier statistics on component mount
  useEffect(() => {
    fetchSupplierStatistics();
  }, []);

  const fetchSupplierStatistics = async () => {
    try {
      const response = await AdminSupplierAPI.getSupplierStatistics({
        period: '30d'
      });

      if (response.success) {
        setSupplierStats(response.data);
      } else {
        console.error('Failed to fetch supplier statistics:', response.message);
      }
    } catch (error) {
      console.error('Error fetching supplier statistics:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setIsLoading(true);
      const response = await AdminSupplierAPI.getAllSuppliers({
        search: debouncedSearchQuery || undefined,
        verificationStatus: verificationStatusFilter !== 'all' ? verificationStatusFilter : undefined,
        isActive: activeStatusFilter !== 'all' ? activeStatusFilter === 'active' : undefined,
        page: currentPage,
        limit: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        includeStats: true
      });


      if (response.success) {
        // The suppliers are in response.data.suppliers
        const suppliersList = response.data.suppliers;
        const paginationData = response.data.pagination;

        // Update pagination state
        if (paginationData) {
          setTotalPages(paginationData.pages);
          setTotalSuppliers(paginationData.total);
        }

        // Set the suppliers list
        if (Array.isArray(suppliersList)) {
          setSuppliers(suppliersList);
        } else {
          console.warn('Expected suppliers array but got:', suppliersList);
          setSuppliers([]);
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch suppliers",
          variant: "destructive"
        });
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast({
        title: "Error",
        description: "Failed to fetch suppliers. Please try again.",
        variant: "destructive"
      });
      setSuppliers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change for new supplier form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSupplier(prev => ({
      ...prev,
      [name]: value
    }));
  };



  // Handle form submission to add new supplier
  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      // Create supplier request object
      const supplierData: SupplierCreateRequest = {
        businessName: newSupplier.businessName,
        ownerName: newSupplier.ownerName,
        email: newSupplier.email,
        mobile: newSupplier.mobile,
        password: newSupplier.password,
        gstNumber: newSupplier.gstNumber || undefined,
        panNumber: newSupplier.panNumber || undefined,
        address: {
          street: newSupplier.street,
          city: newSupplier.city,
          state: newSupplier.state,
          country: newSupplier.country,
          pincode: newSupplier.pincode
        }
      };

      const response = await SupplierAPI.registerSupplier(supplierData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Supplier created successfully"
        });

        // Refresh the suppliers list
        fetchSuppliers();

        // Reset form and close modal
        setNewSupplier({
          businessName: '',
          ownerName: '',
          email: '',
          mobile: '',
          password: '',
          gstNumber: '',
          panNumber: '',
          street: '',
          city: '',
          state: '',
          country: 'India',
          pincode: ''
        });
        setIsAddModalOpen(false);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to create supplier",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error creating supplier:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create supplier",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle supplier deletion (deactivation)
  const handleDeleteSupplier = (supplierId: string) => {
    setDeactivateSupplierId(supplierId);
  };

  const confirmDeactivateSupplier = async () => {
    if (!deactivateSupplierId) return;

    const supplierId = deactivateSupplierId;

    try {
      setIsDeleting(supplierId);
      const response = await AdminSupplierAPI.deleteSupplier(supplierId, {
        permanent: false,
        reason: "Deactivated by admin"
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Supplier deactivated successfully"
        });

        // Remove from local state
        setSuppliers(prev => prev.filter(s => s._id !== supplierId));
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to deactivate supplier",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deactivating supplier:", error);
      toast({
        title: "Error",
        description: "Failed to deactivate supplier. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
      setDeactivateSupplierId(null);
    }
  };

  // Handle toggling supplier active status
  const handleToggleActive = async (supplierId: string) => {
    try {
      setIsToggling(supplierId);
      const supplier = suppliers.find(s => s._id === supplierId);
      if (!supplier) return;

      const response = await AdminSupplierAPI.updateSupplier(supplierId, {
        isActive: !supplier.isActive,
        adminNotes: `Status changed to ${!supplier.isActive ? 'active' : 'inactive'} by admin`
      });

      if (response.success) {
        toast({
          title: "Success",
          description: `Supplier ${response.data.isActive ? 'activated' : 'deactivated'} successfully`
        });

        // Update in local state
        setSuppliers(prev => prev.map(s => s._id === supplierId ? response.data : s));

        // If we're in the detail view, update the selected supplier too
        if (selectedSupplier && selectedSupplier._id === supplierId) {
          setSelectedSupplier(response.data);
        }
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update supplier status",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error toggling supplier status:", error);
      toast({
        title: "Error",
        description: "Failed to update supplier status. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsToggling(null);
    }
  };

  // Handle viewing supplier details
  const handleViewSupplier = async (supplierId: string) => {
    try {
      setIsLoading(true);
      const response = await AdminSupplierAPI.getSupplierById(supplierId);

      if (response.success) {
        setSelectedSupplier(response.data);
        // Fetch products when viewing supplier details
        fetchSupplierProducts(supplierId);
        // Fetch orders when viewing supplier details
        fetchSupplierOrders(supplierId);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch supplier details",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching supplier details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch supplier details. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle editing supplier
  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
  };

  // Fetch supplier products
  const fetchSupplierProducts = async (supplierId: string) => {
    try {
      setIsLoadingProducts(true);
      const response = await AdminSupplierAPI.getSupplierProducts(supplierId);

      if (response.success && response.data.products) {
        setSupplierProducts(response.data.products);
      } else {
        setSupplierProducts([]);
      }
    } catch (error) {
      console.error("Error fetching supplier products:", error);
      setSupplierProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Fetch supplier orders
  const fetchSupplierOrders = async (supplierId: string) => {
    try {
      setIsLoadingOrders(true);
      const response = await AdminSupplierAPI.getSupplierOrderHistory(supplierId, {
        page: orderCurrentPage,
        limit: orderPageSize,
        status: orderStatusFilter !== 'all' ? orderStatusFilter : undefined
      });

      if (response.success && response.data.orders) {
        setSupplierOrders(response.data.orders);
        setOrderTotalPages(response.data.pagination?.pages || 1);
        setOrderTotalCount(response.data.pagination?.total || 0);
      } else {
        setSupplierOrders([]);
        setOrderTotalPages(1);
        setOrderTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching supplier orders:", error);
      setSupplierOrders([]);
      setOrderTotalPages(1);
      setOrderTotalCount(0);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // Handle order status filter change
  const handleOrderStatusFilterChange = (status: string) => {
    setOrderStatusFilter(status);
    setOrderCurrentPage(1);
    if (selectedSupplier) {
      fetchSupplierOrders(selectedSupplier._id);
    }
  };

  // Handle order pagination
  const handleOrderPageChange = (newPage: number) => {
    setOrderCurrentPage(newPage);
    if (selectedSupplier) {
      fetchSupplierOrders(selectedSupplier._id);
    }
  };

  // Handle viewing order details
  const handleViewOrderDetails = async (orderId: string) => {
    try {
      // Fetch order details including route data
      const response = await OrderAPI.getOrder(orderId);

      if (response.success) {
        setSelectedOrder(response.data);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch order details",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch order details. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'processing':
        return 'default';
      case 'confirmed':
        return 'default';
      case 'shipped':
        return 'outline';
      case 'delivered':
        return 'default';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Readable status label: "in_transit" -> "In Transit".
  const formatStatusLabel = (status?: string) =>
    (status || '')
      .split('_')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');

  // Friendly payment-method label.
  const formatPaymentMethod = (m?: string) => {
    const map: Record<string, string> = {
      cash: 'Cash on Delivery',
      cod: 'Cash on Delivery',
      online: 'Online Payment',
      razorpay: 'Online (Razorpay)',
      bank_transfer: 'Bank Transfer',
      wallet: 'Wallet',
    };
    return m ? (map[m.toLowerCase()] || formatStatusLabel(m)) : '';
  };


  // Helper function to check if supplier is verified
  const isVerified = (supplier: Supplier) => {
    return supplier.verificationStatus === 'verified';
  };

  // Inside the component, before the return statement
  // Add a safeguard to ensure suppliers is always an array
  useEffect(() => {
    // This will run after every render
    // If suppliers is not an array, reset it to an empty array
    if (!Array.isArray(suppliers)) {
      console.error('Suppliers state is not an array:', suppliers);
      setSuppliers([]);
    }
  }, [suppliers]);

  // Handle verifying a supplier
  const handleVerifySupplier = async (supplierId: string) => {
    try {
      setIsLoading(true);
      const response = await AdminSupplierAPI.updateSupplier(supplierId, {
        verificationStatus: 'verified',
        adminNotes: 'Verified by admin'
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Supplier verified successfully"
        });

        // Update in local state
        if (selectedSupplier && selectedSupplier._id === supplierId) {
          setSelectedSupplier(response.data);
        }
        setSuppliers(prev => prev.map(s => s._id === supplierId ? response.data : s));
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to verify supplier",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error verifying supplier:", error);
      toast({
        title: "Error",
        description: "Failed to verify supplier. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle updating a supplier
  const handleUpdateSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSupplier) return;

    try {
      setIsSubmitting(true);

      const updateData = {
        businessName: editingSupplier.businessName,
        ownerName: editingSupplier.ownerName,
        email: editingSupplier.email,
        mobile: editingSupplier.mobile,
        gstNumber: editingSupplier.gstNumber,
        panNumber: editingSupplier.panNumber,
        address: {
          street: editingSupplier.address?.street || '',
          city: editingSupplier.address?.city || '',
          state: editingSupplier.address?.state || '',
          country: editingSupplier.address?.country || 'India',
          pincode: editingSupplier.address?.pincode || ''
        },
        adminNotes: editingSupplier.adminNotes
      };

      const response = await AdminSupplierAPI.updateSupplier(editingSupplier._id, updateData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Supplier updated successfully"
        });

        // Update in local state
        if (selectedSupplier && selectedSupplier._id === editingSupplier._id) {
          setSelectedSupplier(response.data);
        }
        setSuppliers(prev => prev.map(s => s._id === editingSupplier._id ? response.data : s));
        setEditingSupplier(null);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update supplier",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating supplier:", error);
      toast({
        title: "Error",
        description: "Failed to update supplier. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the suppliers page
  return (
    <div className="flex flex-col h-full">
      {/* Main view when no supplier is selected */}
      {!selectedSupplier ? (
        <>
          {/* Fixed Header section - stays at top */}
          <div className="flex-shrink-0 space-y-4 sm:space-y-6 p-4 sm:p-6 bg-background border-b">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Suppliers</h1>
                <p className="text-muted-foreground text-sm sm:text-base">Manage your supplier network and partnerships</p>
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Supplier
              </Button>
            </div>

            {/* Stats cards */}
            {isLoading ? (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="border rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-16" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <Skeleton className="h-10 w-10 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <StatsCard
                  title="Total Suppliers"
                  value={supplierStats?.totalSuppliers || totalSuppliers}
                  icon={<Users className="h-6 w-6" />}
                  iconClassName="bg-primary/10 text-primary"
                />
                <StatsCard
                  title="Active Suppliers"
                  value={supplierStats?.activeSuppliers || suppliers.filter(s => s.isActive).length}
                  icon={<UserCheck className="h-6 w-6" />}
                  iconClassName="bg-green-100 text-green-600"
                />
                <StatsCard
                  title="Verified Suppliers"
                  value={supplierStats?.verifiedSuppliers || suppliers.filter(s => s.verificationStatus === 'verified').length}
                  icon={<CheckCircle className="h-6 w-6" />}
                  iconClassName="bg-blue-100 text-blue-600"
                />
                <StatsCard
                  title="Pending Verification"
                  value={supplierStats?.pendingVerification || suppliers.filter(s => s.verificationStatus === 'pending').length}
                  icon={<Clock className="h-6 w-6" />}
                  iconClassName="bg-yellow-100 text-yellow-600"
                />
              </div>
            )}

            {/* Search and filters */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search suppliers..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Enhanced filters */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="verification-filter">Verification Status</Label>
                  <Select value={verificationStatusFilter} onValueChange={setVerificationStatusFilter}>
                    <SelectTrigger id="verification-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="active-filter">Active Status</Label>
                  <Select value={activeStatusFilter} onValueChange={setActiveStatusFilter}>
                    <SelectTrigger id="active-filter">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Suppliers</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort-by">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger id="sort-by">
                      <SelectValue placeholder="Business Name" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="businessName">Business Name</SelectItem>
                      <SelectItem value="ownerName">Owner Name</SelectItem>
                      <SelectItem value="createdAt">Registration Date</SelectItem>
                      <SelectItem value="verificationStatus">Verification Status</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort-order">Sort Order</Label>
                  <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as 'asc' | 'desc')}>
                    <SelectTrigger id="sort-order">
                      <SelectValue placeholder="Ascending" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Supplier List Container */}
          <div className="px-6 pb-6">
            <div>
              <ResponsiveSupplierList
                suppliers={suppliers}
                isLoading={isLoading}
                onView={handleViewSupplier}
                onEdit={handleEditSupplier}
                onDelete={handleDeleteSupplier}
                onToggleActive={handleToggleActive}
                onAddProduct={(supplier) => {
                  setSelectedSupplier(supplier);
                  setIsAddProductModalOpen(true);
                }}
                isDeleting={isDeleting}
                isToggling={isToggling}
              />
            </div>

            {/* Fixed Pagination at bottom */}
            {!isLoading && suppliers.length > 0 && (
              <div className="flex-shrink-0 p-4 border-t bg-background">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  summary={`Showing ${suppliers.length} of ${totalSuppliers} suppliers`}
                />
              </div>
            )}
          </div>

          {/* Add Supplier Modal */}
          {isAddModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center border-b pb-4 mb-4">
                    <h2 className="text-2xl font-bold">Add New Supplier</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsAddModalOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  <form onSubmit={handleAddSupplier} className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Business Information</h3>

                      <div>
                        <Label htmlFor="businessName">Business Name *</Label>
                        <Input
                          id="businessName"
                          name="businessName"
                          value={newSupplier.businessName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="ownerName">Owner Name *</Label>
                        <Input
                          id="ownerName"
                          name="ownerName"
                          value={newSupplier.ownerName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={newSupplier.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="mobile">Mobile *</Label>
                        <Input
                          id="mobile"
                          name="mobile"
                          type="tel"
                          value={newSupplier.mobile}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="password">Password *</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          value={newSupplier.password}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="gstNumber">GST Number</Label>
                        <Input
                          id="gstNumber"
                          name="gstNumber"
                          value={newSupplier.gstNumber}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div>
                        <Label htmlFor="panNumber">PAN Number</Label>
                        <Input
                          id="panNumber"
                          name="panNumber"
                          value={newSupplier.panNumber}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Address</h3>

                      <div>
                        <Label htmlFor="street">Street *</Label>
                        <Input
                          id="street"
                          name="street"
                          value={newSupplier.street}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={newSupplier.city}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={newSupplier.state}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="country">Country *</Label>
                        <Input
                          id="country"
                          name="country"
                          value={newSupplier.country}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="pincode">Pincode *</Label>
                        <Input
                          id="pincode"
                          name="pincode"
                          value={newSupplier.pincode}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-span-2 flex justify-end space-x-2 pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddModalOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          'Add Supplier'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Edit Supplier Modal */}
          {editingSupplier && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center border-b pb-4 mb-4">
                    <h2 className="text-2xl font-bold">Edit Supplier</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingSupplier(null)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  <form onSubmit={handleUpdateSupplier} className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Business Information</h3>

                      <div>
                        <Label htmlFor="edit-businessName">Business Name *</Label>
                        <Input
                          id="edit-businessName"
                          name="businessName"
                          value={editingSupplier.businessName}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, businessName: e.target.value })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-ownerName">Owner Name *</Label>
                        <Input
                          id="edit-ownerName"
                          name="ownerName"
                          value={editingSupplier.ownerName}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, ownerName: e.target.value })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-email">Email *</Label>
                        <Input
                          id="edit-email"
                          name="email"
                          type="email"
                          value={editingSupplier.email}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-mobile">Mobile *</Label>
                        <Input
                          id="edit-mobile"
                          name="mobile"
                          value={editingSupplier.mobile}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, mobile: e.target.value })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-gstNumber">GST Number</Label>
                        <Input
                          id="edit-gstNumber"
                          name="gstNumber"
                          value={editingSupplier.gstNumber || ''}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, gstNumber: e.target.value })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-panNumber">PAN Number</Label>
                        <Input
                          id="edit-panNumber"
                          name="panNumber"
                          value={editingSupplier.panNumber || ''}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, panNumber: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Address Information</h3>

                      <div>
                        <Label htmlFor="edit-street">Street Address *</Label>
                        <Input
                          id="edit-street"
                          name="street"
                          value={editingSupplier.address?.street || ''}
                          onChange={(e) => setEditingSupplier({
                            ...editingSupplier,
                            address: { ...editingSupplier.address, street: e.target.value }
                          })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-city">City *</Label>
                        <Input
                          id="edit-city"
                          name="city"
                          value={editingSupplier.address?.city || ''}
                          onChange={(e) => setEditingSupplier({
                            ...editingSupplier,
                            address: { ...editingSupplier.address, city: e.target.value }
                          })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-state">State *</Label>
                        <Input
                          id="edit-state"
                          name="state"
                          value={editingSupplier.address?.state || ''}
                          onChange={(e) => setEditingSupplier({
                            ...editingSupplier,
                            address: { ...editingSupplier.address, state: e.target.value }
                          })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-country">Country *</Label>
                        <Input
                          id="edit-country"
                          name="country"
                          value={editingSupplier.address?.country || 'India'}
                          onChange={(e) => setEditingSupplier({
                            ...editingSupplier,
                            address: { ...editingSupplier.address, country: e.target.value }
                          })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-pincode">Pincode *</Label>
                        <Input
                          id="edit-pincode"
                          name="pincode"
                          value={editingSupplier.address?.pincode || ''}
                          onChange={(e) => setEditingSupplier({
                            ...editingSupplier,
                            address: { ...editingSupplier.address, pincode: e.target.value }
                          })}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-adminNotes">Admin Notes</Label>
                        <textarea
                          id="edit-adminNotes"
                          name="adminNotes"
                          className="w-full p-2 border rounded-md"
                          rows={3}
                          value={editingSupplier.adminNotes || ''}
                          onChange={(e) => setEditingSupplier({ ...editingSupplier, adminNotes: e.target.value })}
                          placeholder="Add any admin notes about this supplier..."
                        />
                      </div>
                    </div>

                    <div className="col-span-2 flex justify-end space-x-2 pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditingSupplier(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          'Update Supplier'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        // Supplier dashboard view when a supplier is selected
        <div className="h-full">
          <div className="p-4 border-b flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setSelectedSupplier(null)}
            >
              Back to Suppliers
            </Button>
            <h1 className="text-lg sm:text-2xl font-bold truncate max-w-md">{selectedSupplier.businessName}</h1>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => handleVerifySupplier(selectedSupplier._id)}
                disabled={isVerified(selectedSupplier)}
              >
                {isVerified(selectedSupplier) ? 'Verified' : 'Verify'}
              </Button>
            </div>
          </div>

          {/* Enhanced Supplier Dashboard with tabs */}
          <div className="p-6">
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="products">Products</TabsTrigger>
                <TabsTrigger value="orders">Orders</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-card rounded-lg shadow p-6 border space-y-4">
                    <h2 className="text-lg sm:text-xl font-semibold flex items-center">
                      <User className="h-5 w-5 mr-2 text-primary" />
                      Contact Information
                    </h2>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <Mail className="h-4 w-4 mr-2 text-muted-foreground mt-1 flex-shrink-0" />
                        <div className="overflow-hidden">
                          <div className="font-medium">Email</div>
                          <div className="break-words">{selectedSupplier.email}</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-medium">Phone</div>
                          <div>{selectedSupplier.mobile}</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 mr-2 text-muted-foreground mt-1 flex-shrink-0" />
                        <div className="overflow-hidden">
                          <div className="font-medium">Address</div>
                          <div className="break-words">
                            {selectedSupplier.address?.street}<br />
                            {selectedSupplier.address?.city}, {selectedSupplier.address?.state}<br />
                            {selectedSupplier.address?.country} - {selectedSupplier.address?.pincode}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg shadow p-6 border space-y-4">
                    <h2 className="text-lg sm:text-xl font-semibold flex items-center">
                      <Package className="h-5 w-5 mr-2 text-primary" />
                      Business Details
                    </h2>
                    <div className="space-y-2">
                      <div>
                        <div className="font-medium">Owner</div>
                        <div>{selectedSupplier.ownerName}</div>
                      </div>
                      {selectedSupplier.gstNumber && (
                        <div>
                          <div className="font-medium">GST Number</div>
                          <div className="break-words">{selectedSupplier.gstNumber}</div>
                        </div>
                      )}
                      {selectedSupplier.panNumber && (
                        <div>
                          <div className="font-medium">PAN Number</div>
                          <div>{selectedSupplier.panNumber}</div>
                        </div>
                      )}
                      <div>
                        <div className="font-medium">Status</div>
                        <Button
                          variant={selectedSupplier.isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleToggleActive(selectedSupplier._id)}
                          className="mt-1 w-24"
                        >
                          {selectedSupplier.isActive ? 'Active' : 'Inactive'}
                        </Button>
                      </div>
                      <div>
                        <div className="font-medium">Verification</div>
                        {isVerified(selectedSupplier) ? (
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Verified
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-1"
                              onClick={() => handleVerifySupplier(selectedSupplier._id)}
                            >
                              Verify Supplier
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg shadow p-6 border space-y-4">
                    <h2 className="text-lg sm:text-xl font-semibold flex items-center">
                      <Package className="h-5 w-5 mr-2 text-primary" />
                      Quick Stats
                    </h2>
                    <div className="space-y-2">
                      <div>
                        <div className="font-medium">Total Products</div>
                        <div className="text-2xl font-bold">{supplierProducts.length || selectedSupplier.products?.length || 0}</div>
                      </div>
                      <div>
                        <div className="font-medium">Total Orders</div>
                        <div className="text-2xl font-bold">{orderTotalCount}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="products" className="space-y-4">
                <div className="bg-card rounded-lg shadow p-6 border">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg sm:text-xl font-semibold">Products ({supplierProducts.length})</h2>
                    <Button onClick={() => setIsAddProductModalOpen(true)}>Add Product</Button>
                  </div>
                  {isLoadingProducts ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="border rounded-lg p-4">
                          <Skeleton className="h-4 w-3/4 mb-2" />
                          <Skeleton className="h-3 w-1/2 mb-4" />
                          <Skeleton className="h-6 w-20" />
                        </div>
                      ))}
                    </div>
                  ) : supplierProducts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {supplierProducts.map((product: any) => (
                        <div key={product._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="font-medium mb-1">
                            {product.product?.name || product.name}
                          </div>
                          <div className="text-sm text-muted-foreground mb-2">
                            Category: {product.product?.category?.name || 'N/A'}
                          </div>
                          <div className="text-sm mb-2">
                            <span className="font-medium">Price:</span> ₹{product.price || 0}
                          </div>
                          <div className="text-sm mb-3">
                            <span className="font-medium">Stock:</span> {product.inventory?.currentStock || 0} {product.product?.unit || ''}
                          </div>
                          <div className="flex justify-between items-center">
                            <StatusBadge status={product.isActive ? 'active' : 'inactive'} />
                            <Badge variant={product.inventory?.isLowStock ? "destructive" : product.inventory?.isOutOfStock ? "destructive" : "outline"}>
                              {product.inventory?.isOutOfStock ? "Out of Stock" : product.inventory?.isLowStock ? "Low Stock" : "In Stock"}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                      <p>No products available from this supplier.</p>
                      <Button className="mt-4" onClick={() => setIsAddProductModalOpen(true)}>Add First Product</Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="orders" className="space-y-4">
                <div className="bg-card rounded-lg shadow p-6 border">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg sm:text-xl font-semibold">Order History ({orderTotalCount})</h2>
                    <div className="flex space-x-2">
                      <Select value={orderStatusFilter} onValueChange={handleOrderStatusFilterChange}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Filter by Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Orders</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline">Export</Button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {isLoadingOrders ? (
                      <div className="space-y-4">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="border rounded-lg p-4">
                            <div className="flex justify-between items-center">
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-24" />
                              </div>
                              <div className="space-y-2 text-right">
                                <Skeleton className="h-4 w-20" />
                                <Skeleton className="h-5 w-16" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : supplierOrders.length > 0 ? (
                      <>
                        {supplierOrders.map((order) => (
                          <div key={order._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewOrderDetails(order._id)}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <div className="font-medium">Order #{order.orderNumber}</div>
                                  <Badge variant={getStatusBadgeVariant(order.status)}>
                                    {formatStatusLabel(order.status)}
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                                  <div>
                                    <div className="flex items-center">
                                      <Calendar className="h-4 w-4 mr-1" />
                                      {formatDate(order.createdAt)}
                                    </div>
                                    <div className="flex items-center mt-1">
                                      <User className="h-4 w-4 mr-1" />
                                      {order.user?.name || 'Unknown User'}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="flex items-center">
                                      <Package className="h-4 w-4 mr-1" />
                                      {order.items?.length || 0} items
                                    </div>
                                    {order.deliveryAddress && (
                                      <div className="flex items-center mt-1">
                                        <MapPin className="h-4 w-4 mr-1" />
                                        {order.deliveryAddress.city}, {order.deliveryAddress.state}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-medium text-lg">{formatCurrency(order.totalAmount)}</div>
                                <div className="text-sm text-muted-foreground">
                                  {order.paymentMethod && (
                                    <div className="flex items-center justify-end">
                                      <CreditCard className="h-4 w-4 mr-1" />
                                      {formatPaymentMethod(order.paymentMethod)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {order.items && order.items.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <div className="text-sm text-muted-foreground">
                                  <strong>Items:</strong> {order.items.map((item: any) => item.product?.name || 'Unknown Product').join(', ')}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Pagination for orders */}
                        {orderTotalPages > 1 && (
                          <div className="flex justify-between items-center pt-4 border-t">
                            <div className="text-sm text-muted-foreground">
                              Showing {supplierOrders.length} of {orderTotalCount} orders
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={orderCurrentPage === 1}
                                onClick={() => handleOrderPageChange(orderCurrentPage - 1)}
                              >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                              </Button>
                              <div className="flex items-center space-x-1">
                                {Array.from({ length: Math.min(orderTotalPages, 5) }, (_, i) => {
                                  let page: number;
                                  if (orderTotalPages <= 5) {
                                    page = i + 1;
                                  } else if (orderCurrentPage <= 3) {
                                    page = i + 1;
                                  } else if (orderCurrentPage >= orderTotalPages - 2) {
                                    page = orderTotalPages - 4 + i;
                                  } else {
                                    page = orderCurrentPage - 2 + i;
                                  }
                                  return (
                                    <Button
                                      key={page}
                                      variant={orderCurrentPage === page ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => handleOrderPageChange(page)}
                                    >
                                      {page}
                                    </Button>
                                  );
                                })}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={orderCurrentPage === orderTotalPages}
                                onClick={() => handleOrderPageChange(orderCurrentPage + 1)}
                              >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                        <p className="font-medium">No orders found</p>
                        <p className="text-sm">This supplier has no orders yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="analytics" className="space-y-4">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="bg-muted/50 p-4 rounded-full mb-4">
                    <TrendingUp className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold">Analytics Coming Soon</h3>
                  <p className="text-muted-foreground max-w-sm mt-2">
                    Detailed analytics and performance metrics for this supplier will be available in a future update.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {selectedSupplier && (
        <SupplierProductCreateModalForSupplier
          isOpen={isAddProductModalOpen}
          onClose={() => setIsAddProductModalOpen(false)}
          onSuccess={() => {
            // Refresh supplier products after successful creation
            fetchSupplierProducts(selectedSupplier._id);
          }}
          supplier={selectedSupplier}
        />
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
      />
      {/* Deactivation Confirmation Dialog */}
      <AlertDialog open={!!deactivateSupplierId} onOpenChange={(open) => !open && setDeactivateSupplierId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate this supplier? They will no longer be able to access the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeactivateSupplierId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeactivateSupplier}>
              {isDeleting === deactivateSupplierId ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deactivating...
                </>
              ) : (
                'Deactivate'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
